import { useMemo, useState } from "react";
import { Autocomplete, Box, Chip, Paper, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { GridColDef } from "@mui/x-data-grid";
import { ServerDataGrid, useDataGridState } from "@safrater/ui";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";

interface InstLite { id: string; name: string }
interface UserLite { id: string; name: string; email: string }
interface AuditRow {
  id: string; action: string; entity: string; entityId: string | null; institutionId: string | null;
  ip: string | null; data: Record<string, unknown>; createdAt: string;
  actor: { id: string; name: string; email: string } | null;
}

const ts = (s: string) => new Date(s).toLocaleString("pt-BR");

/**
 * Trilha de auditoria do Central. Filtros por instituição e usuário em
 * Autocomplete (busca digitando), período por data, e o grid no padrão do
 * design-system (`ServerDataGrid`) com busca rápida, ordenação e paginação
 * server-side. A busca rápida vai como `q` (ação/entidade/autor).
 */
export function AuditoriaPage() {
  const { user } = useAuth();
  const state = useDataGridState({ sort: [{ field: "createdAt", sort: "desc" }] });

  // Filtros-facet explícitos (fora do grid). Ao mudar, volta para a 1ª página.
  const [institutionId, setInstitutionId] = useState("");
  const [actorId, setActorId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const onFacet = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    state.setPagination({ ...state.pagination, page: 0 });
  };

  const { data: institutions } = useQuery({
    queryKey: ["institutions-lite-audit"],
    queryFn: async () => (await api.get<{ data: InstLite[] }>("/institutions", { params: { page_size: 500 } })).data.data,
  });
  const { data: users } = useQuery({
    queryKey: ["users-lite-audit"],
    queryFn: async () => (await api.get<{ data: UserLite[] }>("/users", { params: { page_size: 500 } })).data.data,
  });

  const instById = useMemo(() => new Map((institutions ?? []).map((i) => [i.id, i.name])), [institutions]);

  const { data, isFetching } = useQuery({
    queryKey: ["audit", state.queryParams, institutionId, actorId, from, to],
    queryFn: async () => (await api.get<{ data: AuditRow[]; total: number }>("/audit", {
      params: {
        ...state.queryParams,
        institutionId: institutionId || undefined,
        actorId: actorId || undefined,
        from: from || undefined,
        to: to ? `${to}T23:59:59` : undefined,
      },
    })).data,
    placeholderData: (p) => p,
  });

  const columns: GridColDef<AuditRow>[] = [
    { field: "createdAt", headerName: "Data/hora", width: 170, filterable: false, valueFormatter: (v) => ts(v as string) },
    {
      field: "actor", headerName: "Usuário", flex: 1, minWidth: 180, sortable: false, filterable: false,
      valueGetter: (_v, row) => row.actor?.name ?? "—",
    },
    {
      field: "action", headerName: "Ação", width: 180,
      renderCell: ({ value }) => <Chip size="small" label={value as string} variant="outlined" />,
    },
    {
      field: "entity", headerName: "Entidade", flex: 1, minWidth: 160,
      valueGetter: (_v, row) => `${row.entity}${row.entityId ? ` · ${row.entityId.slice(0, 8)}` : ""}`,
    },
    {
      field: "institutionId", headerName: "Instituição", flex: 1, minWidth: 180, sortable: false, filterable: false,
      valueGetter: (v) => (v ? instById.get(v as string) ?? (v as string).slice(0, 8) : "—"),
    },
    { field: "ip", headerName: "IP", width: 130, sortable: false, filterable: false, valueGetter: (v) => (v as string) ?? "—" },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Auditoria</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={institutions ?? []}
            getOptionLabel={(o) => o.name}
            value={(institutions ?? []).find((i) => i.id === institutionId) ?? null}
            onChange={(_e, v) => onFacet(setInstitutionId)(v?.id ?? "")}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label="Instituição" placeholder="Todas" />}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 300 }}
            options={users ?? []}
            getOptionLabel={(o) => `${o.name} (${o.email})`}
            value={(users ?? []).find((u) => u.id === actorId) ?? null}
            onChange={(_e, v) => onFacet(setActorId)(v?.id ?? "")}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label="Usuário" placeholder="Todos" />}
          />
          <TextField label="De" type="date" size="small" value={from} onChange={(e) => onFacet(setFrom)(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Até" type="date" size="small" value={to} onChange={(e) => onFacet(setTo)(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Stack>
      </Paper>

      <Box sx={{ height: 640 }}>
        <ServerDataGrid<AuditRow>
          gridId="central.audit"
          userScope={user?.sub}
          rows={data?.data ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          paginationModel={state.pagination}
          onPaginationModelChange={state.setPagination}
          sortModel={state.sort}
          onSortModelChange={state.setSort}
          filterModel={state.filter}
          onFilterModelChange={state.setFilter}
          emptyMessage="Nenhum evento no filtro."
        />
      </Box>
    </Box>
  );
}
